import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, formatDate } from "../lib/api";
import { Plus, Trash2, X, Eye, EyeOff, KeyRound, ShieldCheck, MessageCircle } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { GRANTABLE_PAGES } from "../lib/pages";

const blank = { email: "", password: "", name: "", role: "staff" };

export default function Users() {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [pwEdit, setPwEdit] = useState(null); // { id, name, value }
  const [pwSaving, setPwSaving] = useState(false);
  const [permEdit, setPermEdit] = useState(null); // { id, name, selected: [] }
  const [permSaving, setPermSaving] = useState(false);

  function openPerm(u) { setPermEdit({ id: u.id, name: u.name, selected: u.permissions || [] }); }
  function togglePerm(key) {
    setPermEdit((p) => ({ ...p, selected: p.selected.includes(key) ? p.selected.filter((k) => k !== key) : [...p.selected, key] }));
  }
  async function savePerm() {
    setPermSaving(true);
    try {
      await api.patch(`/users/${permEdit.id}/permissions`, { permissions: permEdit.selected });
      toast.success(`Access updated for ${permEdit.name}`);
      setPermEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update access"); }
    finally { setPermSaving(false); }
  }

  const load = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function save(e) {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      await api.post("/users", form);
      setShowForm(false);
      setForm(blank);
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  }

  async function remove(id) {
    if (!confirm("Delete user?")) return;
    await api.delete(`/users/${id}`); load();
  }

  async function savePassword(e) {
    e.preventDefault();
    setPwSaving(true);
    try {
      await api.patch(`/users/${pwEdit.id}/password`, { password: pwEdit.value });
      toast.success(`Password updated for ${pwEdit.name}`);
      setPwEdit(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update password");
    } finally { setPwSaving(false); }
  }

  async function setWa(u) {
    const num = window.prompt(`WhatsApp number for ${u.name} (with country code, e.g. +919812345678). Leave blank to remove. GARV will chat with this number on WhatsApp.`, u.whatsapp_number || "");
    if (num === null) return;
    try {
      const { data } = await api.patch(`/users/${u.id}/whatsapp`, { whatsapp_number: num.trim() });
      toast.success(data.whatsapp_number ? `WhatsApp mapped for ${u.name}` : `WhatsApp removed for ${u.name}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save WhatsApp number"); }
  }

  return (
    <div data-testid="users-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Admin</div>
          <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1">Users</h1>
        </div>
        <button className="btn-primary" onClick={()=>{setForm(blank); setErr(""); setShowForm(true);}} data-testid="create-user-button">
          <Plus className="w-4 h-4" /> Add User
        </button>
      </div>

      <div className="table-wrap">
        <table className="w-full">
          <thead>
            <tr><th className="table-th">Name</th><th className="table-th">Email</th><th className="table-th">Password</th><th className="table-th">Role</th><th className="table-th">WhatsApp</th><th className="table-th">Access</th><th className="table-th">Created</th><th className="table-th text-right">Actions</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="table-row" data-testid={`user-row-${u.id}`}>
                <td className="table-td font-medium"><button className="text-[#C84B31] hover:underline" onClick={()=>navigate(`/staff/${u.id}`)} data-testid={`user-name-link-${u.id}`}>{u.name}</button></td>
                <td className="table-td">{u.email}</td>
                <td className="table-td font-mono text-xs">
                  {u.password_plain ? (
                    <span className="inline-flex items-center gap-2">
                      <span data-testid={`user-password-${u.id}`}>{revealed[u.id] ? u.password_plain : "••••••••"}</span>
                      <button className="text-[#68665E] hover:text-[#C84B31]" onClick={()=>setRevealed((p)=>({...p, [u.id]: !p[u.id]}))} data-testid={`user-reveal-${u.id}`} title={revealed[u.id] ? "Hide" : "Show"}>
                        {revealed[u.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </span>
                  ) : (
                    <span className="text-[#A09E96]" title="This user's password predates password storage — set a new one to view it.">— set to view</span>
                  )}
                </td>
                <td className="table-td"><span className={u.role === "admin" ? "badge-success" : "badge-neutral"}>{u.role}</span></td>
                <td className="table-td font-mono text-xs" data-testid={`user-whatsapp-value-${u.id}`}>{u.whatsapp_number || <span className="text-[#A09E96]">—</span>}</td>
                <td className="table-td text-xs" data-testid={`user-access-${u.id}`}>{u.role === "admin" ? <span className="text-[#2F6F4E] font-medium">Full access</span> : <span className="text-[#68665E]">Dashboard + {(u.permissions || []).length} page(s)</span>}</td>
                <td className="table-td text-[#68665E]">{formatDate(u.created_at)}</td>
                <td className="table-td text-right whitespace-nowrap">
                  {u.role !== "admin" && (
                    <button className="btn-ghost" onClick={()=>openPerm(u)} data-testid={`user-permissions-${u.id}`} title="Manage page access"><ShieldCheck className="w-4 h-4" /></button>
                  )}
                  <button className="btn-ghost" onClick={()=>setPwEdit({ id: u.id, name: u.name, value: "" })} data-testid={`user-edit-password-${u.id}`} title="Set / change password"><KeyRound className="w-4 h-4" /></button>
                  <button className="btn-ghost" onClick={()=>setWa(u)} data-testid={`user-whatsapp-${u.id}`} title="Map WhatsApp number for GARV bot"><MessageCircle className="w-4 h-4" /></button>
                  {u.id !== me?.id && (
                    <button className="btn-ghost text-[#8A2526]" onClick={()=>remove(u.id)} data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-start justify-center py-16" data-testid="user-form-modal">
          <div className="bg-white w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-[#E5E3DB] flex items-center justify-between">
              <h2 className="font-headings text-xl font-bold">Add User</h2>
              <button className="btn-ghost" onClick={()=>setShowForm(false)}><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div><label className="input-label">Name</label><input className="input-field" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} required data-testid="user-name-input" /></div>
              <div><label className="input-label">Email</label><input type="email" className="input-field" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} required data-testid="user-email-input" /></div>
              <div><label className="input-label">Password</label><input type="text" minLength={6} className="input-field font-mono" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} required data-testid="user-password-input" /></div>
              <div><label className="input-label">Role</label>
                <select className="input-field" value={form.role} onChange={(e)=>setForm({...form, role: e.target.value})} data-testid="user-role-select">
                  <option value="staff">Staff</option><option value="admin">Admin</option>
                </select>
              </div>
              {err && <div className="badge-error w-full justify-center py-2" data-testid="user-form-error">{err}</div>}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" className="btn-secondary" onClick={()=>setShowForm(false)}>Cancel</button>
                <button type="submit" disabled={saving} className="btn-primary" data-testid="user-save-button">{saving ? "Saving..." : "Create User"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pwEdit && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-start justify-center py-16" data-testid="pw-edit-modal">
          <div className="bg-white w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-[#E5E3DB] flex items-center justify-between">
              <h2 className="font-headings text-xl font-bold">Set Password — {pwEdit.name}</h2>
              <button className="btn-ghost" onClick={()=>setPwEdit(null)}><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={savePassword} className="p-6 space-y-4">
              <div>
                <label className="input-label">New Password</label>
                <input type="text" minLength={6} className="input-field font-mono" value={pwEdit.value} onChange={(e)=>setPwEdit({...pwEdit, value: e.target.value})} required placeholder="At least 6 characters" data-testid="pw-edit-input" />
                <p className="text-[11px] text-[#68665E] mt-1">The user can log in with this new password immediately. It will be visible here to admins.</p>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" className="btn-secondary" onClick={()=>setPwEdit(null)}>Cancel</button>
                <button type="submit" disabled={pwSaving} className="btn-primary" data-testid="pw-edit-save">{pwSaving ? "Saving..." : "Update Password"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {permEdit && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-start justify-center py-16 overflow-y-auto" data-testid="perm-edit-modal">
          <div className="bg-white w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-[#E5E3DB] flex items-center justify-between">
              <h2 className="font-headings text-xl font-bold">Page Access — {permEdit.name}</h2>
              <button className="btn-ghost" onClick={()=>setPermEdit(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-[11px] text-[#68665E]">Dashboard is always visible. Tick the pages this staff member can see and open.</p>
              <div className="grid grid-cols-1 gap-2 max-h-80 overflow-y-auto">
                {GRANTABLE_PAGES.map((p) => (
                  <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`perm-option-${p.key}`}>
                    <input type="checkbox" checked={permEdit.selected.includes(p.key)} onChange={()=>togglePerm(p.key)} data-testid={`perm-check-${p.key}`} />
                    {p.label}
                  </label>
                ))}
              </div>
              <div className="flex items-center justify-between pt-2">
                <div className="flex gap-2">
                  <button type="button" className="btn-ghost text-xs" onClick={()=>setPermEdit({...permEdit, selected: GRANTABLE_PAGES.map((p)=>p.key)})} data-testid="perm-select-all">Select all</button>
                  <button type="button" className="btn-ghost text-xs" onClick={()=>setPermEdit({...permEdit, selected: []})} data-testid="perm-clear-all">Clear all</button>
                </div>
                <div className="flex gap-3">
                  <button type="button" className="btn-secondary" onClick={()=>setPermEdit(null)}>Cancel</button>
                  <button type="button" disabled={permSaving} className="btn-primary" onClick={savePerm} data-testid="perm-save">{permSaving ? "Saving..." : "Save Access"}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
